import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../../context/ThemeContext'
import api from '../../services/api'
import toast from 'react-hot-toast'
import MessagesPage from '../../components/MessagesPage'
import TestAccountBadge from '../../components/TestAccountBadge'
import ConfirmAdmissionPaymentModal, { isAdmissionFeeInvoice } from '../../components/finance/ConfirmAdmissionPaymentModal'
import {
  DollarSign,
  Search,
  Plus,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Receipt,
  FileText,
  AlertCircle,
  Calendar,
  LogOut,
  Moon,
  Sun,
  Edit,
  Trash2,
  BarChart2,
  RefreshCw,
  Menu,
  X,
  Users,
  Filter,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  User,
  Lock,
  Save,
  EyeOff,
  ShoppingCart,
  Tag
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import ReceiptModal from '../../components/finance/ReceiptModal'
import InvoiceModal from '../../components/finance/InvoiceModal'
import PaymentRunwayTracker from '../../components/finance/PaymentRunwayTracker'

const ITEMS_PER_PAGE = 10

// ─── Stat Card — Card-free ───────────────────────────────────────────────────
function StatCard({ title, value, sub, icon: Icon, accent }) {
  const accents = {
    green:  'text-green-400',
    orange: 'text-cinema-glow',
    yellow: 'text-yellow-400',
    red:    'text-red-400',
    blue:   'text-blue-400',
    purple: 'text-purple-400',
  }
  const iconColor = accents[accent] || accents.blue
  return (
    <div className="cinema-stat">
      <div className="flex items-center gap-2">
        <Icon size={16} className={`${iconColor} opacity-80`} />
        <p className="cinema-stat-label">{title}</p>
      </div>
      <p className="cinema-stat-value text-xl sm:text-2xl">{value}</p>
      {sub && <p className="cinema-stat-sub">{sub}</p>}
    </div>
  )
}

// ─── Pagination — Card-free ──────────────────────────────────────────────────
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-center gap-3 mt-6">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 text-cinema-muted hover:text-white disabled:opacity-30 transition-all"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="font-sans text-xs text-cinema-muted tracking-widest uppercase">
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 text-cinema-muted hover:text-white disabled:opacity-30 transition-all"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}

// ─── Status badge helpers — Cinematic ───────────────────────────────────────
const statusBadge = {
  paid:      'text-green-400',
  pending:   'text-yellow-400',
  partial:   'text-blue-400',
  overdue:   'text-red-400',
  cancelled: 'text-cinema-muted',
}
const statusIcon = {
  paid:    <CheckCircle className="w-3 h-3" />,
  pending: <Clock className="w-3 h-3" />,
  partial: <AlertCircle className="w-3 h-3" />,
  overdue: <XCircle className="w-3 h-3" />,
}
const formatCurrency = (n) => `UGX ${Number(n || 0).toLocaleString()}`

export default function FinanceDashboard() {
  const navigate = useNavigate()
  const { user, logout, updateUser } = useAuthStore()

  // ─── UI state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('overview')

  // ─── Profile modal state ─────────────────────────────────────────────────
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const [showCurPwd, setShowCurPwd] = useState(false)
  const [showNewPwd, setShowNewPwd] = useState(false)
  const [showConPwd, setShowConPwd] = useState(false)
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', phone: '', email: '' })
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' })

  const openProfileModal = async () => {
    setShowProfileModal(true)
    try {
      const { data } = await api.get('/auth/profile')
      setProfileForm({ first_name: data.first_name || '', last_name: data.last_name || '', phone: data.phone || '', email: data.email || '' })
    } catch { toast.error('Failed to load profile') }
  }

  const handleSaveProfileInfo = async (e) => {
    e.preventDefault()
    setProfileSaving(true)
    try {
      await api.put('/auth/profile', { first_name: profileForm.first_name, last_name: profileForm.last_name, phone: profileForm.phone })
      updateUser({ first_name: profileForm.first_name, last_name: profileForm.last_name })
      toast.success('Profile updated successfully')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update profile')
    } finally { setProfileSaving(false) }
  }

  const handleSavePassword = async (e) => {
    e.preventDefault()
    if (!pwdForm.current_password || !pwdForm.new_password || !pwdForm.confirm_password) return toast.error('All password fields are required')
    if (pwdForm.new_password !== pwdForm.confirm_password) return toast.error('New passwords do not match')
    if (pwdForm.new_password.length < 6) return toast.error('New password must be at least 6 characters')
    setProfileSaving(true)
    try {
      await api.put('/auth/profile', { current_password: pwdForm.current_password, new_password: pwdForm.new_password })
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' })
      toast.success('Password changed successfully')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to change password')
    } finally { setProfileSaving(false) }
  }
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  // ─── Data ─────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState({
    totalRevenue: 0, pendingAmount: 0, paidAmount: 0, overdueAmount: 0,
    totalInvoices: 0, paidInvoices: 0, pendingInvoices: 0, overdueInvoices: 0,
    totalExpenses: 0, expenseCount: 0, netProfit: 0
  })
  const [invoices, setInvoices] = useState([])
  const [payments, setPayments] = useState([])
  const [students, setStudents] = useState([])
  const [courses, setCourses] = useState([])
  const [expenses, setExpenses] = useState([])
  const [submissions, setSubmissions] = useState([])

  // ─── Payment Submissions Review state ──────────────────────────────────────
  const [submissionFilter, setSubmissionFilter] = useState('PENDING_VERIFICATION')
  const [submissionSearchTerm, setSubmissionSearchTerm] = useState('')
  const [rejectModal, setRejectModal] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [processingSubId, setProcessingSubId] = useState(null)
  const [proofLoadingId, setProofLoadingId] = useState(null)

  // ─── Expense state ────────────────────────────────────────────────────────
  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [editingExpenseId, setEditingExpenseId] = useState(null)
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('')
  const [expenseFilterCategory, setExpenseFilterCategory] = useState('all')
  const [expenseForm, setExpenseForm] = useState({
    title: '', category: 'other', amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash', vendor: '', receipt_reference: '', notes: ''
  })

  const EXPENSE_CATEGORIES = {
    rent_utilities: 'Rent & Utilities', salaries: 'Salaries',
    equipment_supplies: 'Equipment & Supplies', marketing: 'Marketing',
    maintenance: 'Maintenance', software_technology: 'Software & Technology',
    administrative: 'Administrative', other: 'Other'
  }

  // ─── Invoice state ────────────────────────────────────────────────────────
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [isEditInvoice, setIsEditInvoice] = useState(false)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [invoiceForm, setInvoiceForm] = useState({
    student_id: '', course_id: '', amount: '', due_date: '', description: ''
  })

  // ─── Payment state ────────────────────────────────────────────────────────
  const [editingPaymentId, setEditingPaymentId] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [admissionPaymentInvoice, setAdmissionPaymentInvoice] = useState(null)
  const [paymentForm, setPaymentForm] = useState({
    invoice_id: '', amount: '', payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    transaction_reference: '', notes: ''
  })

  // ─── Filters & pagination ─────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [selectedReceiptPaymentId, setSelectedReceiptPaymentId] = useState(null)
  const [previewInvoice, setPreviewInvoice] = useState(null)

  const handleDownloadReceipt = (payment) => {
    const paymentId = payment.id || payment.payment_id
    if (!paymentId) {
      toast.error('Payment ID not found')
      return
    }
    // Open the receipt modal — the shared ReceiptDocument is rendered there,
    // and PDF / PNG / Print all export that exact DOM. This guarantees the
    // download matches the on-screen preview.
    setSelectedReceiptPaymentId(paymentId)
  }

  const handleViewInvoiceReceipt = (invoice) => {
    const invPayments = payments.filter(
      p => p.invoice_id === invoice.id || p.invoice_number === invoice.invoice_number
    )
    if (invPayments.length > 0) {
      setSelectedReceiptPaymentId(invPayments[0].id)
    } else {
      toast.error('No payment records found for this invoice yet')
    }
  }

  // ─── Report state ─────────────────────────────────────────────────────────
  const [reportPeriod, setReportPeriod] = useState('month')

  // ─── Fetch data ───────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true)
    try {
      const [statsRes, invRes, payRes, stuRes, courRes, expRes, subRes] = await Promise.all([
        api.get('/finance/stats'),
        api.get('/finance/invoices'),
        api.get('/finance/payments'),
        api.get('/finance/students'),
        api.get('/finance/courses'),
        api.get('/finance/expenses'),
        api.get('/finance/payment-submissions').catch(() => ({ data: { submissions: [] } })),
      ])
      setStats(statsRes.data)
      setInvoices(invRes.data.invoices || [])
      setPayments(payRes.data.payments || [])
      setStudents(stuRes.data || [])
      setCourses(courRes.data || [])
      setExpenses(expRes.data.expenses || [])
      setSubmissions(subRes.data?.submissions || [])
    } catch (err) {
      console.error(err)
      toast.error('Failed to load financial data')
    } finally {
      setLoading(false)
    }
  }

  const handleApproveSubmission = async (sub) => {
    if (!window.confirm(`Approve payment of ${formatCurrency(sub.amount)} for ${sub.first_name} ${sub.last_name}? This will record the payment in the authoritative ledger.`)) {
      return
    }
    setProcessingSubId(sub.id)
    try {
      await api.post(`/finance/payment-submissions/${sub.id}/verify`, {
        review_notes: 'Approved via Finance Portal'
      })
      toast.success('Payment approved and posted to ledger!')
      fetchData()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to approve payment')
    } finally {
      setProcessingSubId(null)
    }
  }

  const handleRejectSubmission = async (e) => {
    e.preventDefault()
    if (!rejectModal || !rejectReason.trim()) {
      return toast.error('Please enter a rejection reason')
    }
    setProcessingSubId(rejectModal.id)
    try {
      await api.post(`/finance/payment-submissions/${rejectModal.id}/reject`, {
        review_notes: rejectReason.trim()
      })
      toast.success('Payment submission rejected')
      setRejectModal(null)
      setRejectReason('')
      fetchData()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to reject payment')
    } finally {
      setProcessingSubId(null)
    }
  }

  // T14.2: Fetch a short-lived presigned URL for viewing a payment proof
  const handleViewProof = async (submissionId) => {
    setProofLoadingId(submissionId)
    try {
      const res = await api.get(`/finance/payment-submissions/${submissionId}/proof-url`)
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('No proof file available for this submission.')
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to retrieve proof file.'
      toast.error(msg)
    } finally {
      setProofLoadingId(null)
    }
  }

  useEffect(() => { fetchData() }, [])

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const generateRef = (method, invoice = null) => {
    const ts = Date.now().toString().slice(-8)
    const pre = { cash: 'CASH', mobile_money: 'MM', bank_transfer: 'BANK', card: 'CARD', other: 'TXN' }[method] || 'TXN'
    if (invoice) {
      const num = invoice.invoice_number?.replace('INV-', '') || 'XXXX'
      const code = invoice.course_code?.substring(0, 4).toUpperCase() || 'GEN'
      return `${pre}-${code}-${num}-${ts}`
    }
    return `${pre}-${ts}`
  }

  // ─── Invoice actions ──────────────────────────────────────────────────────
  const handleOpenCreateInvoice = () => {
    const due = new Date(); due.setMonth(due.getMonth() + 1)
    setInvoiceForm({ student_id: '', course_id: '', amount: '', due_date: due.toISOString().split('T')[0], description: '' })
    setIsEditInvoice(false); setSelectedInvoice(null); setShowInvoiceModal(true)
  }

  const handleEditInvoice = (inv) => {
    setSelectedInvoice(inv); setIsEditInvoice(true)
    setInvoiceForm({
      student_id: inv.student_id, course_id: inv.course_id || '',
      amount: inv.amount, due_date: inv.due_date.split('T')[0],
      description: inv.description || '', status: inv.status
    })
    setShowInvoiceModal(true)
  }

  const handleSaveInvoice = async (e) => {
    e.preventDefault()
    try {
      const data = {
        ...invoiceForm,
        student_id: parseInt(invoiceForm.student_id),
        course_id: invoiceForm.course_id ? parseInt(invoiceForm.course_id) : null,
        amount: parseFloat(invoiceForm.amount)
      }
      if (isEditInvoice && selectedInvoice) {
        await api.put(`/finance/invoices/${selectedInvoice.id}`, data)
        toast.success('Invoice updated')
      } else {
        await api.post('/finance/invoices', data)
        toast.success('Invoice created')
      }
      setShowInvoiceModal(false); setIsEditInvoice(false); setSelectedInvoice(null)
      setInvoiceForm({ student_id: '', course_id: '', amount: '', due_date: '', description: '' })
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save invoice')
    }
  }

  const handleDeleteInvoice = async (id) => {
    try {
      await api.delete(`/finance/invoices/${id}`)
      toast.success('Invoice deleted'); setDeleteConfirm(null); fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete invoice')
    }
  }

  // ─── Payment actions ──────────────────────────────────────────────────────
  const handleOpenPayment = (invoice = null) => {
    // Route unpaid admission fee invoices directly to quick confirmation dialog
    if (invoice && isAdmissionFeeInvoice(invoice) && invoice.status !== 'paid') {
      setAdmissionPaymentInvoice(invoice)
      return
    }

    const method = 'cash'
    setEditingPaymentId(null); setSelectedInvoice(invoice)
    setPaymentForm({
      invoice_id: invoice?.id || '', amount: '',
      payment_method: method,
      payment_date: new Date().toISOString().split('T')[0],
      transaction_reference: generateRef(method, invoice), notes: ''
    })
    setShowPaymentModal(true)
  }

  const handleEditPayment = (payment) => {
    setEditingPaymentId(payment.id)
    setPaymentForm({
      invoice_id: payment.invoice_id, amount: payment.amount,
      payment_method: payment.payment_method,
      payment_date: payment.payment_date.split('T')[0],
      transaction_reference: payment.transaction_reference || '',
      notes: payment.notes || ''
    })
    setShowPaymentModal(true)
  }

  const handleSavePayment = async (e) => {
    e.preventDefault()
    try {
      if (editingPaymentId) {
        await api.put(`/finance/payments/${editingPaymentId}`, paymentForm)
        toast.success('Payment updated')
      } else {
        await api.post('/finance/payments', paymentForm)
        toast.success('Payment recorded')
      }
      setShowPaymentModal(false); setEditingPaymentId(null)
      setPaymentForm({ invoice_id: '', amount: '', payment_method: 'cash', payment_date: new Date().toISOString().split('T')[0], transaction_reference: '', notes: '' })
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save payment')
    }
  }

  const handleDeletePayment = async (id) => {
    try {
      await api.delete(`/finance/payments/${id}`)
      toast.success('Payment deleted'); setDeleteConfirm(null); fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete payment')
    }
  }

  // ─── Expense actions ──────────────────────────────────────────────────────
  const resetExpenseForm = () => {
    setExpenseForm({ title: '', category: 'other', amount: '', expense_date: new Date().toISOString().split('T')[0], payment_method: 'cash', vendor: '', receipt_reference: '', notes: '' })
    setEditingExpenseId(null); setShowExpenseModal(false)
  }

  const handleSaveExpense = async (e) => {
    e.preventDefault()
    try {
      const payload = { ...expenseForm, amount: parseFloat(expenseForm.amount) }
      if (editingExpenseId) {
        await api.put(`/finance/expenses/${editingExpenseId}`, payload)
        toast.success('Expense updated')
      } else {
        await api.post('/finance/expenses', payload)
        toast.success('Expense recorded')
      }
      resetExpenseForm(); fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save expense')
    }
  }

  const handleEditExpense = (exp) => {
    setEditingExpenseId(exp.id)
    setExpenseForm({
      title: exp.title, category: exp.category, amount: exp.amount,
      expense_date: exp.expense_date.split('T')[0], payment_method: exp.payment_method,
      vendor: exp.vendor || '', receipt_reference: exp.receipt_reference || '', notes: exp.notes || ''
    })
    setShowExpenseModal(true)
  }

  const handleDeleteExpense = async (id) => {
    try {
      await api.delete(`/finance/expenses/${id}`)
      toast.success('Expense deleted'); setDeleteConfirm(null); fetchData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete expense')
    }
  }

  // ─── Download Invoice ─────────────────────────────────────────────────────
  // Opens the invoice preview modal. PDF / PNG / Print all export the exact
  // same DOM shown in the preview, so the download can never drift from it.
  const downloadInvoice = (invoice) => {
    setPreviewInvoice(invoice)
  }

  // ─── Filtered invoices ────────────────────────────────────────────────────
  const filteredInvoices = invoices.filter(inv => {
    const q = searchTerm.toLowerCase()
    return (
      (inv.student_name?.toLowerCase().includes(q) ||
        inv.invoice_number?.toLowerCase().includes(q) ||
        inv.course_title?.toLowerCase().includes(q)) &&
      (filterStatus === 'all' || inv.status === filterStatus)
    )
  })
  const totalInvPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE)
  const currentInvoices = filteredInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  // ─── Report helpers ────────────────────────────────────────────────────────
  const getReportPayments = () => {
    const now = new Date()
    return payments.filter(p => {
      const d = new Date(p.payment_date)
      if (reportPeriod === 'week') {
        const diff = (now - d) / (1000 * 60 * 60 * 24)
        return diff <= 7
      }
      if (reportPeriod === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
      if (reportPeriod === 'year') return d.getFullYear() === now.getFullYear()
      return true
    })
  }

  const reportPayments = getReportPayments()
  const reportTotal = reportPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0)

  const methodTotals = reportPayments.reduce((acc, p) => {
    acc[p.payment_method] = (acc[p.payment_method] || 0) + parseFloat(p.amount || 0)
    return acc
  }, {})

  const handleLogout = () => { logout(); navigate('/login') }

  // ─── Sidebar nav items ────────────────────────────────────────────────────
  const pendingCount = submissions.filter(s => s.status === 'PENDING_VERIFICATION').length
  const navItems = [
    { id: 'overview', label: 'Overview', icon: DollarSign },
    { id: 'submissions', label: 'Submissions', icon: CheckCircle, badge: pendingCount },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'runway', label: 'Payment Runway', icon: Clock },
    { id: 'expenses', label: 'Expenses', icon: ShoppingCart },
    { id: 'reports', label: 'Reports', icon: BarChart2 },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ]

  const inputCls = 'w-full px-3 py-2.5 bg-white/5 border-b border-white/10 text-white text-sm font-sans placeholder:text-cinema-muted focus:outline-none focus:border-cinema-gold/40 focus:bg-white/8 transition-colors'
  const selectCls = 'w-full px-3 py-2.5 bg-[#1a1a1a] border-b border-white/10 text-white text-sm font-sans focus:outline-none focus:border-cinema-gold/40 transition-colors'

  return (
    <div className="cinema-page min-h-screen">
      <div className="cinema-grain" aria-hidden="true" />
      <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-30 fixed" />
      <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-0 -left-20 opacity-20 fixed" />

      {/* ─── Top Nav ─── */}
      <nav className="sticky top-0 z-30 bg-cinema-black/90 border-b border-white/5">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 lg:hidden text-cinema-muted hover:text-white transition-colors"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <img src="/Logos/SMA LOGO  DARK MODE.png" alt="SMA Finance Portal" className="h-8" />
              <div className="hidden md:flex flex-col">
                <span className="font-display text-white font-medium text-sm leading-none">Finance Portal</span>
                <span className="cinema-label mt-1 text-xs">Synergy Multimedia Academy</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={openProfileModal}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-cinema-soft hover:text-white transition-all"
                title="Edit profile"
              >
                <User size={14} className="text-cinema-gold" />
                <span className="font-sans text-xs">{user?.first_name} {user?.last_name}</span>
              </button>
              <button
                onClick={fetchData}
                className="p-2 text-cinema-muted hover:text-white transition-all"
                title="Refresh"
              >
                <RefreshCw size={15} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 text-cinema-muted hover:text-red-400 text-xs font-sans transition-all"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/70 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        {/* ─── Sidebar ─── */}
        <aside className={`
          fixed top-16 left-0 bottom-0 w-60 z-40 overflow-y-auto transition-transform duration-300 ease-in-out
          border-r border-white/5 p-4 bg-cinema-black/80
          lg:static lg:translate-x-0 lg:flex-shrink-0 lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="space-y-1">
            {navItems.map(({ id, label, icon: Icon, badge }) => (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setSidebarOpen(false) }}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-sans transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-cinema-glow/10 text-cinema-glow'
                    : 'text-cinema-muted hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={16} />
                  {label}
                </div>
                {badge > 0 && (
                  <span className="bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 text-xs px-2 py-0.5 rounded-full font-mono font-medium">
                    {badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="mt-6 cinema-divider" />

          {/* Quick Stats */}
          <div className="mt-6 space-y-3">
            <p className="cinema-label block px-1">Quick Summary</p>
            {[
              { label: 'Revenue', value: stats.totalRevenue, color: 'text-green-400' },
              { label: 'Expenses', value: stats.totalExpenses, color: 'text-red-400' },
              { label: 'Net Profit', value: Math.abs(stats.netProfit || 0), color: (stats.netProfit || 0) >= 0 ? 'text-blue-400' : 'text-red-400' },
              { label: 'Pending', value: stats.pendingAmount, color: 'text-yellow-400' },
              { label: 'Overdue', value: stats.overdueAmount, color: 'text-red-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex justify-between items-center px-1">
                <span className="font-sans text-xs text-cinema-muted font-medium">{label}</span>
                <span className={`font-display text-xs font-semibold ${color}`}>
                  {(value || 0).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* ─── Main Content ─── */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0 relative">
          {loading ? <LoadingSpinner size="md" className="py-8" label="Loading financial data" /> : (
            <>
              {/* ═══ OVERVIEW ═══ */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="cinema-label block mb-2">Finance Portal</span>
                      <h1 className="font-display text-2xl sm:text-3xl font-light text-white">Financial Overview</h1>
                    </div>
                    <button
                      onClick={handleOpenCreateInvoice}
                      className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0"
                    >
                      <Plus size={14} /> New Invoice
                    </button>
                  </div>

                  {/* Stats grid — top row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    <StatCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} sub={`${stats.totalInvoices} total invoices`} icon={DollarSign} accent="green" />
                    <StatCard title="Total Expenses" value={formatCurrency(stats.totalExpenses)} sub={`${stats.expenseCount} expense records`} icon={ShoppingCart} accent="red" />
                    <StatCard title="Net Profit" value={formatCurrency(Math.abs(stats.netProfit || 0))} sub={(stats.netProfit || 0) >= 0 ? 'Revenue surplus' : 'Running at a loss'} icon={(stats.netProfit || 0) >= 0 ? TrendingUp : TrendingDown} accent={(stats.netProfit || 0) >= 0 ? 'blue' : 'red'} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard title="Amount Paid" value={formatCurrency(stats.paidAmount)} sub={`${stats.paidInvoices} invoices paid`} icon={CheckCircle} accent="blue" />
                    <StatCard title="Pending" value={formatCurrency(stats.pendingAmount)} sub={`${stats.pendingInvoices} invoices pending`} icon={Clock} accent="yellow" />
                    <StatCard title="Overdue" value={formatCurrency(stats.overdueAmount)} sub={`${stats.overdueInvoices} overdue invoices`} icon={AlertCircle} accent="red" />
                  </div>

                  {/* Recent activity */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent invoices */}
                    <div className="border-b border-white/6 pb-2">
                      <div className="flex justify-between items-center px-6 py-4">
                        <h2 className="font-display text-base text-white font-light">Recent Invoices</h2>
                        <button onClick={() => setActiveTab('invoices')} className="cinema-btn-text text-xs font-medium">View all →</button>
                      </div>
                      <div>
                        {invoices.slice(0, 6).map(inv => (
                          <div key={inv.id} className="flex items-center justify-between px-6 py-3.5 border-b border-white/4 hover:bg-white/3 transition-colors">
                            <div>
                              <p className="font-sans text-sm text-white font-medium">{inv.invoice_number}</p>
                              <p className="font-sans text-xs text-cinema-muted mt-0.5 flex items-center gap-1.5 font-medium">
                                {inv.student_name}
                                <TestAccountBadge show={inv.is_test_account} />
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-display text-sm text-white font-semibold">{formatCurrency(inv.amount)}</p>
                              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 mt-1 font-sans uppercase tracking-wide font-medium rounded-sm ${statusBadge[inv.status] || statusBadge.pending}`}>
                                {statusIcon[inv.status]}{inv.status}
                              </span>
                            </div>
                          </div>
                        ))}
                        {invoices.length === 0 && (
                          <div className="px-6 py-10 text-center">
                            <FileText size={28} className="mx-auto mb-3 text-cinema-muted/30" />
                            <p className="font-sans text-sm text-cinema-muted">No invoices yet</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recent payments */}
                    <div className="border-b border-white/6 pb-2">
                      <div className="flex justify-between items-center px-6 py-4">
                        <h2 className="font-display text-base text-white font-light">Recent Payments</h2>
                        <button onClick={() => setActiveTab('payments')} className="cinema-btn-text text-xs font-medium">View all →</button>
                      </div>
                      <div>
                        {payments.slice(0, 6).map(pay => (
                          <div key={pay.id} className="flex items-center justify-between px-6 py-3.5 border-b border-white/4 hover:bg-white/3 transition-colors">
                            <div>
                              <p className="font-sans text-sm text-white font-medium flex items-center gap-1.5">
                                {pay.student_name}
                                <TestAccountBadge show={pay.is_test_account} />
                              </p>
                              <p className="font-sans text-xs text-cinema-muted mt-0.5 font-medium">
                                {new Date(pay.payment_date).toLocaleDateString()} · {pay.payment_method?.replace(/_/g, ' ')}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="font-display text-sm text-green-400 font-semibold">{formatCurrency(pay.amount)}</p>
                              <button
                                onClick={() => setSelectedReceiptPaymentId(pay.id)}
                                className="p-1.5 text-cinema-gold hover:text-white bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/20 rounded-sm transition-colors"
                                title="View Receipt"
                              >
                                <Receipt size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                        {payments.length === 0 && (
                          <div className="px-6 py-10 text-center">
                            <Receipt size={28} className="mx-auto mb-3 text-cinema-muted/30" />
                            <p className="font-sans text-xs text-cinema-muted">No payments yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div>
                    <div className="cinema-divider mb-8" />
                    <h2 className="font-display text-base text-white font-light mb-5">Quick Actions</h2>
                    <div className="flex flex-wrap gap-3">
                      <button onClick={handleOpenCreateInvoice} className="cinema-btn-primary text-xs px-4 py-2 min-h-0"><Plus size={13} /> New Invoice</button>
                      <button onClick={() => handleOpenPayment()} className="flex items-center gap-1.5 px-4 py-2 text-xs font-sans text-green-400 border border-green-500/30 hover:bg-green-500/10 transition-colors"><Plus size={13} /> Record Payment</button>
                      <button onClick={() => { resetExpenseForm(); setShowExpenseModal(true) }} className="flex items-center gap-1.5 px-4 py-2 text-xs font-sans text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors"><Plus size={13} /> Add Expense</button>
                      <button onClick={() => setActiveTab('reports')} className="cinema-btn-ghost text-xs px-4 py-2 min-h-0"><BarChart2 size={13} /> View Reports</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ SUBMISSIONS (VERIFICATION QUEUE) ═══ */}
              {activeTab === 'submissions' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div>
                      <span className="cinema-label block mb-2">Finance Portal</span>
                      <h1 className="font-display text-2xl sm:text-3xl font-light text-white">Payment Submissions</h1>
                      <p className="text-xs text-cinema-muted mt-1 font-sans">
                        Student self-reported bank / mobile money payments awaiting verification
                      </p>
                    </div>

                    {/* Filter tabs */}
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { id: 'PENDING_VERIFICATION', label: 'Pending' },
                        { id: 'VERIFIED', label: 'Verified' },
                        { id: 'REJECTED', label: 'Rejected' },
                        { id: 'ALL', label: 'All' },
                      ].map(f => (
                        <button
                          key={f.id}
                          onClick={() => setSubmissionFilter(f.id)}
                          className={`px-3 py-1.5 text-xs font-sans rounded-sm transition-all ${
                            submissionFilter === f.id
                              ? 'bg-cinema-gold/20 text-cinema-gold border border-cinema-gold/40'
                              : 'text-cinema-muted hover:text-white bg-white/5'
                          }`}
                        >
                          {f.label}
                          {f.id === 'PENDING_VERIFICATION' && pendingCount > 0 && (
                            <span className="ml-1.5 px-1.5 py-0.2 bg-yellow-500/30 text-yellow-300 text-xs rounded-full font-mono">
                              {pendingCount}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search submissions..."
                      value={submissionSearchTerm}
                      onChange={e => setSubmissionSearchTerm(e.target.value)}
                      className={`${inputCls} pl-9`}
                    />
                  </div>

                  {/* Submissions table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/6">
                          {['Date', 'Student', 'Invoice #', 'Amount', 'Method', 'Reference', 'Proof', 'Status', 'Notes', 'Actions'].map(h => (
                            <th key={h} className="px-4 py-3 text-left cinema-label">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/4">
                        {submissions
                          .filter(s => {
                            if (submissionFilter !== 'ALL' && s.status !== submissionFilter) return false
                            if (submissionSearchTerm) {
                              const q = submissionSearchTerm.toLowerCase()
                              const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase()
                              const inv = (s.invoice_number || '').toLowerCase()
                              const ref = (s.transaction_reference || '').toLowerCase()
                              return name.includes(q) || inv.includes(q) || ref.includes(q)
                            }
                            return true
                          })
                          .map(sub => (
                            <tr key={sub.id} className="hover:bg-white/3 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-xs text-cinema-soft">
                                {new Date(sub.submitted_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <p className="font-sans text-sm text-white font-medium flex items-center gap-1.5">
                                  {sub.first_name} {sub.last_name}
                                  <TestAccountBadge show={sub.is_test_account} />
                                </p>
                                <p className="font-sans text-xs text-cinema-muted">{sub.student_number || sub.email}</p>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-xs text-cinema-soft">
                                {sub.invoice_number}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap font-display text-sm text-green-400 font-semibold">
                                {formatCurrency(sub.amount)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-xs text-cinema-soft capitalize">
                                {sub.payment_method?.replace(/_/g, ' ')}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-cinema-muted">
                                {sub.transaction_reference || '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {sub.has_proof ? (
                                  <button
                                    onClick={() => handleViewProof(sub.id)}
                                    disabled={proofLoadingId === sub.id}
                                    className="text-xs text-cinema-gold hover:text-white transition-colors underline"
                                  >
                                    {proofLoadingId === sub.id ? 'Loading...' : 'View Proof'}
                                  </button>
                                ) : (
                                  <span className="text-xs text-cinema-muted/50">None</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide rounded-sm ${
                                  sub.status === 'VERIFIED'
                                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                    : sub.status === 'REJECTED'
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                                    : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                }`}>
                                  {sub.status === 'VERIFIED' ? <CheckCircle size={11} /> : sub.status === 'REJECTED' ? <XCircle size={11} /> : <Clock size={11} />}
                                  {sub.status === 'PENDING_VERIFICATION' ? 'Pending' : sub.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-cinema-muted max-w-[130px] truncate font-sans">
                                {sub.review_notes || sub.notes || '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {sub.status === 'PENDING_VERIFICATION' ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      disabled={processingSubId === sub.id}
                                      onClick={() => handleApproveSubmission(sub)}
                                      className="px-2.5 py-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs border border-green-500/30 rounded-sm font-sans"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      disabled={processingSubId === sub.id}
                                      onClick={() => { setRejectModal(sub); setRejectReason('') }}
                                      className="px-2.5 py-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs border border-red-500/30 rounded-sm font-sans"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                ) : sub.status === 'VERIFIED' && sub.payment_id ? (
                                  <button
                                    onClick={() => setSelectedReceiptPaymentId(sub.payment_id)}
                                    className="px-2 py-1 text-xs text-cinema-gold bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30 rounded-sm inline-flex items-center gap-1"
                                    title="View Receipt"
                                  >
                                    <Receipt size={12} />
                                    <span>Receipt</span>
                                  </button>
                                ) : (
                                  <span className="text-xs text-cinema-muted font-sans">
                                    {sub.reviewer_first_name ? `by ${sub.reviewer_first_name}` : 'Processed'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    {submissions.length === 0 && (
                      <div className="text-center py-12">
                        <FileText size={36} className="mx-auto mb-3 text-cinema-muted/20" />
                        <p className="font-sans text-xs text-cinema-muted">No payment submissions found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ═══ INVOICES ═══ */}
              {activeTab === 'invoices' && (
                <div className="space-y-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="cinema-label block mb-2">Finance Portal</span>
                      <h1 className="font-display text-2xl sm:text-3xl font-light text-white">Invoices</h1>
                    </div>
                    <button onClick={handleOpenCreateInvoice} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0">
                      <Plus size={14} /> Create Invoice
                    </button>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px] relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted w-4 h-4" />
                      <input type="text" placeholder="Search invoices..." value={searchTerm}
                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }}
                        className={`${inputCls} pl-9`} />
                    </div>
                    <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1) }} className={selectCls + ' w-auto'}>
                      <option value="all">All Status</option>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="partial">Partial</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>

                  {/* Invoice table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/6">
                            {['Invoice', 'Student', 'Course', 'Amount', 'Paid', 'Balance', 'Due Date', 'Status', 'Actions'].map(h => (
                              <th key={h} className="px-4 py-3 text-left cinema-label">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/4">
                          {currentInvoices.map(inv => (
                            <tr key={inv.id} className="hover:bg-white/3 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <p className="font-sans text-sm text-white font-medium">{inv.invoice_number}</p>
                                <p className="font-sans text-xs text-cinema-muted">{new Date(inv.created_at).toLocaleDateString()}</p>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <p className="font-sans text-sm text-white flex items-center gap-1.5 font-medium">
                                  {inv.student_name}
                                  <TestAccountBadge show={inv.is_test_account} />
                                </p>
                                <p className="font-sans text-xs text-cinema-muted">{inv.student_email}</p>
                              </td>
                              <td className="px-4 py-3 max-w-[160px] truncate font-sans text-sm text-cinema-soft">{inv.course_title || '—'}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-display text-sm text-white font-medium">{formatCurrency(inv.amount)}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-display text-sm text-green-400">{formatCurrency(inv.amount_paid)}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-display text-sm text-cinema-glow font-semibold">{formatCurrency(inv.amount - (inv.amount_paid || 0))}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft">{new Date(inv.due_date).toLocaleDateString()}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide rounded-sm ${statusBadge[inv.status] || statusBadge.pending}`}>
                                  {statusIcon[inv.status]}{inv.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleOpenPayment(inv)}
                                    className="text-cinema-glow hover:text-cinema-amber transition-colors"
                                    title={isAdmissionFeeInvoice(inv) && inv.status !== 'paid' ? 'Confirm Admission Fee Payment' : 'Record Payment'}
                                  >
                                    <CreditCard size={14} />
                                  </button>
                                  <button onClick={() => handleEditInvoice(inv)} className="text-blue-400 hover:text-blue-300 transition-colors" title="Edit"><Edit size={14} /></button>
                                  <button onClick={() => setDeleteConfirm(`inv-${inv.id}`)} className="text-red-400 hover:text-red-300 transition-colors" title="Delete"><Trash2 size={14} /></button>
                                  <button onClick={() => downloadInvoice(inv)} className="text-cinema-muted hover:text-white transition-colors" title="Download Invoice"><Download size={14} /></button>
                                  {parseFloat(inv.amount_paid || 0) > 0 && (
                                    <button onClick={() => handleViewInvoiceReceipt(inv)} className="text-cinema-gold hover:text-white transition-colors" title="View Payment Receipt"><Receipt size={14} /></button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredInvoices.length === 0 && (
                        <div className="text-center py-12">
                          <FileText size={36} className="mx-auto mb-3 text-cinema-muted/20" />
                          <p className="font-sans text-sm text-cinema-muted">No invoices found</p>
                        </div>
                      )}
                    </div>
                  <Pagination currentPage={currentPage} totalPages={totalInvPages} onPageChange={p => setCurrentPage(p)} />
                </div>
              )}

              {/* ═══ PAYMENTS ═══ */}
              {activeTab === 'payments' && (
                <div className="space-y-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="cinema-label block mb-2">Finance Portal</span>
                      <h1 className="font-display text-2xl sm:text-3xl font-light text-white">Payments</h1>
                    </div>
                    <button onClick={() => handleOpenPayment()} className="flex items-center gap-2 px-5 py-2.5 text-green-400 hover:text-green-300 text-xs font-sans transition-all">
                      <Plus size={14} /> Record Payment
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/6">
                            {['Date', 'Student', 'Invoice', 'Method', 'Amount', 'Reference', 'Notes', 'Actions'].map(h => (
                              <th key={h} className="px-4 py-3 text-left cinema-label">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/4">
                          {payments.map(pay => (
                            <tr key={pay.id} className="hover:bg-white/3 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft">{new Date(pay.payment_date).toLocaleDateString()}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <p className="font-sans text-sm text-white flex items-center gap-1.5 font-medium">
                                  {pay.student_name}
                                  <TestAccountBadge show={pay.is_test_account} />
                                </p>
                                <p className="font-sans text-xs text-cinema-muted">{pay.student_email}</p>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft">{pay.invoice_number}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft capitalize">{pay.payment_method?.replace(/_/g, ' ')}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-display text-sm text-green-400 font-semibold">{formatCurrency(pay.amount)}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-xs text-cinema-muted">{pay.transaction_reference || '—'}</td>
                              <td className="px-4 py-3 max-w-[120px] truncate font-sans text-xs text-cinema-muted">{pay.notes || '—'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => setSelectedReceiptPaymentId(pay.id)} className="text-cinema-gold hover:text-white transition-colors" title="View Official Receipt"><Receipt size={14} /></button>
                                  <button onClick={() => handleDownloadReceipt(pay)} className="text-cinema-soft hover:text-white transition-colors" title="Download Receipt PDF"><Download size={14} /></button>
                                  <button onClick={() => handleEditPayment(pay)} className="text-blue-400 hover:text-blue-300 transition-colors" title="Edit"><Edit size={14} /></button>
                                  <button onClick={() => setDeleteConfirm(`pay-${pay.id}`)} className="text-red-400 hover:text-red-300 transition-colors" title="Delete"><Trash2 size={14} /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {payments.length === 0 && (
                        <div className="text-center py-12">
                          <Receipt size={36} className="mx-auto mb-3 text-cinema-muted/20" />
                          <p className="font-sans text-sm text-cinema-muted">No payments recorded</p>
                        </div>
                      )}
                    </div>
                </div>
              )}

              {/* ═══ EXPENSES ═══ */}
              {activeTab === 'expenses' && (
                <div className="space-y-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="cinema-label block mb-2">Finance Portal</span>
                      <h1 className="font-display text-2xl sm:text-3xl font-light text-white">Expenses</h1>
                    </div>
                    <button onClick={() => { resetExpenseForm(); setShowExpenseModal(true) }} className="flex items-center gap-2 px-5 py-2.5 text-red-400 hover:text-red-300 text-xs font-sans transition-all">
                      <Plus size={14} /> Add Expense
                    </button>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard title="Total Expenses" value={formatCurrency(stats.totalExpenses)} sub={`${stats.expenseCount} records`} icon={ShoppingCart} accent="red" />
                    <StatCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} sub="All collected payments" icon={TrendingUp} accent="green" />
                    <StatCard title="Net Profit" value={formatCurrency(Math.abs(stats.netProfit || 0))} sub={(stats.netProfit || 0) >= 0 ? 'Surplus' : 'Loss'} icon={(stats.netProfit || 0) >= 0 ? TrendingUp : TrendingDown} accent={(stats.netProfit || 0) >= 0 ? 'blue' : 'red'} />
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px] relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted w-4 h-4" />
                      <input type="text" placeholder="Search expenses..." value={expenseSearchTerm} onChange={e => setExpenseSearchTerm(e.target.value)} className={`${inputCls} pl-9`} />
                    </div>
                    <select value={expenseFilterCategory} onChange={e => setExpenseFilterCategory(e.target.value)} className={selectCls + ' w-auto'}>
                      <option value="all">All Categories</option>
                      {Object.entries(EXPENSE_CATEGORIES).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/6">
                            {['#', 'Title', 'Category', 'Amount', 'Date', 'Method', 'Vendor', 'Reference', 'Actions'].map(h => (
                              <th key={h} className="px-4 py-3 text-left cinema-label">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/4">
                          {expenses
                            .filter(exp => {
                              const q = expenseSearchTerm.toLowerCase()
                              return (!q || exp.title?.toLowerCase().includes(q) || exp.vendor?.toLowerCase().includes(q) || exp.expense_number?.toLowerCase().includes(q) || exp.receipt_reference?.toLowerCase().includes(q)) &&
                                (expenseFilterCategory === 'all' || exp.category === expenseFilterCategory)
                            })
                            .map(exp => (
                            <tr key={exp.id} className="hover:bg-white/3 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-xs text-cinema-muted">{exp.expense_number}</td>
                              <td className="px-4 py-3">
                                <p className="font-sans text-sm text-white font-medium">{exp.title}</p>
                                {exp.notes && <p className="font-sans text-xs text-cinema-muted truncate max-w-[140px]">{exp.notes}</p>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 text-xs font-sans font-medium uppercase tracking-wide text-cinema-glow">
                                  <Tag size={11} />{EXPENSE_CATEGORIES[exp.category] || exp.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap font-display text-sm text-red-400 font-semibold">{formatCurrency(exp.amount)}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft">{new Date(exp.expense_date).toLocaleDateString()}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft capitalize">{exp.payment_method?.replace(/_/g, ' ')}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-muted">{exp.vendor || '—'}</td>
                              <td className="px-4 py-3 whitespace-nowrap font-sans text-xs text-cinema-muted">{exp.receipt_reference || '—'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => handleEditExpense(exp)} className="text-blue-400 hover:text-blue-300 transition-colors" title="Edit"><Edit size={14} /></button>
                                  <button onClick={() => setDeleteConfirm(`exp-${exp.id}`)} className="text-red-400 hover:text-red-300 transition-colors" title="Delete"><Trash2 size={14} /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {expenses.length === 0 && (
                        <div className="text-center py-12">
                          <ShoppingCart size={36} className="mx-auto mb-3 text-cinema-muted/20" />
                          <p className="font-sans text-xs text-cinema-muted">No expenses recorded yet</p>
                        </div>
                      )}
                    </div>
                </div>
              )}

              {/* ═══ REPORTS ═══ */}
              {activeTab === 'reports' && (
                <div className="space-y-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="cinema-label block mb-2">Finance Portal</span>
                      <h1 className="font-display text-2xl sm:text-3xl font-light text-white">Financial Reports</h1>
                    </div>
                    <select value={reportPeriod} onChange={e => setReportPeriod(e.target.value)} className={selectCls + ' w-auto'}>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="year">This Year</option>
                      <option value="all">All Time</option>
                    </select>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard title="Period Revenue" value={formatCurrency(reportTotal)} sub={`${reportPayments.length} transactions`} icon={TrendingUp} accent="green" />
                    <StatCard title="Total Invoices" value={stats.totalInvoices} sub={`${stats.paidInvoices} paid · ${stats.pendingInvoices} pending`} icon={FileText} accent="blue" />
                    <StatCard title="Collection Rate" value={`${stats.totalInvoices ? Math.round((stats.paidInvoices / stats.totalInvoices) * 100) : 0}%`} sub="Paid vs total invoices" icon={BarChart2} accent="purple" />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* By payment method */}
                    <div className="border-b border-white/5 pb-6">
                      <h2 className="font-display text-base text-white font-light mb-6">Revenue by Payment Method</h2>
                      {Object.keys(methodTotals).length === 0 ? (
                        <p className="font-sans text-xs text-cinema-muted text-center py-8">No data for selected period</p>
                      ) : (
                        <div className="space-y-4">
                          {Object.entries(methodTotals).sort((a, b) => b[1] - a[1]).map(([method, total]) => {
                            const pct = reportTotal > 0 ? Math.round((total / reportTotal) * 100) : 0
                            return (
                              <div key={method}>
                                <div className="flex justify-between mb-2">
                                  <span className="font-sans text-sm text-cinema-soft capitalize">{method.replace(/_/g, ' ')}</span>
                                  <span className="font-display text-sm text-white font-medium">{formatCurrency(total)} <span className="text-cinema-muted text-xs">({pct}%)</span></span>
                                </div>
                                <div className="w-full h-1.5 bg-white/5 rounded-full">
                                  <div className="bg-cinema-glow h-1.5 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Invoice status breakdown */}
                    <div className="border-b border-white/5 pb-6">
                      <h2 className="font-display text-base text-white font-light mb-6">Invoice Status Breakdown</h2>
                      <div className="space-y-4">
                        {[
                          { label: 'Paid', count: stats.paidInvoices, amount: stats.paidAmount, bar: 'bg-green-500' },
                          { label: 'Pending', count: stats.pendingInvoices, amount: stats.pendingAmount, bar: 'bg-yellow-500' },
                          { label: 'Overdue', count: stats.overdueInvoices, amount: stats.overdueAmount, bar: 'bg-red-500' },
                        ].map(item => {
                          const pct = stats.totalInvoices > 0 ? Math.round((item.count / stats.totalInvoices) * 100) : 0
                          return (
                            <div key={item.label}>
                              <div className="flex justify-between mb-2">
                                <span className="font-sans text-sm text-cinema-soft">{item.label} <span className="text-cinema-muted">({item.count})</span></span>
                                <span className="font-display text-sm text-white font-medium">{formatCurrency(item.amount)} <span className="text-cinema-muted text-xs">({pct}%)</span></span>
                              </div>
                              <div className="w-full h-1.5 bg-white/5 rounded-full">
                                <div className={`${item.bar} h-1.5 rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Transactions table */}
                  <div>
                    <h2 className="font-display text-base text-white font-light mb-4">
                      Transactions — {reportPeriod === 'all' ? 'All Time' : `This ${reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}`}
                    </h2>
                    {reportPayments.length === 0 ? (
                      <div className="text-center py-12 border border-dashed border-white/10">
                        <Receipt size={36} className="mx-auto mb-3 text-cinema-muted/20" />
                        <p className="font-sans text-xs text-cinema-muted">No transactions in selected period</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/6">
                              {['Date', 'Student', 'Invoice', 'Method', 'Reference', 'Amount'].map(h => (
                                <th key={h} className="px-4 py-3 text-left cinema-label">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/4">
                            {reportPayments.map(p => (
                              <tr key={p.id} className="hover:bg-white/3 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft">{new Date(p.payment_date).toLocaleDateString()}</td>
                                <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-white">
                                  <span className="inline-flex items-center gap-1.5">
                                    {p.student_name}
                                    <TestAccountBadge show={p.is_test_account} />
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap font-sans text-sm text-cinema-soft">{p.invoice_number}</td>
                                <td className="px-4 py-3 font-sans text-sm text-cinema-soft capitalize">{p.payment_method?.replace(/_/g, ' ')}</td>
                                <td className="px-4 py-3 font-sans text-xs text-cinema-muted">{p.transaction_reference || '—'}</td>
                                <td className="px-4 py-3 font-display text-sm text-green-400 font-semibold">{formatCurrency(p.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-cinema-glow/20">
                              <td colSpan={5} className="px-4 py-3 font-sans text-xs text-cinema-muted text-right">Total</td>
                              <td className="px-4 py-3 font-display text-sm text-green-400 font-semibold">{formatCurrency(reportTotal)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeTab === 'runway' && <PaymentRunwayTracker dark />}
              {activeTab === 'messages' && (
                <MessagesPage contactsEndpoint="/communication/contacts" title="Messages" />
              )}
            </>
          )}
        </main>
      </div>

      {/* ═══ INVOICE MODAL ═══ */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-elevated w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <span className="cinema-label block mb-1">{isEditInvoice ? 'Edit' : 'New'}</span>
                <h2 className="font-display text-lg text-white font-light">{isEditInvoice ? 'Edit Invoice' : 'Create Invoice'}</h2>
              </div>
              <button onClick={() => setShowInvoiceModal(false)} className="p-2 text-cinema-muted hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <form onSubmit={handleSaveInvoice} className="p-6 space-y-4">
              <div>
                <label className="cinema-label block mb-2">Student *</label>
                <select required value={invoiceForm.student_id} onChange={e => setInvoiceForm({ ...invoiceForm, student_id: e.target.value })} className={selectCls}>
                  <option value="">Select student...</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.student_number})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="cinema-label block mb-2">Course (optional)</label>
                <select value={invoiceForm.course_id} onChange={e => setInvoiceForm({ ...invoiceForm, course_id: e.target.value })} className={selectCls}>
                  <option value="">No course</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title} — {formatCurrency(c.fee)}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="cinema-label block mb-2">Amount (UGX) *</label>
                  <input required type="number" min="0" value={invoiceForm.amount} onChange={e => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} className={inputCls} placeholder="0" />
                </div>
                <div>
                  <label className="cinema-label block mb-2">Due Date *</label>
                  <input required type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="cinema-label block mb-2">Description</label>
                <input type="text" value={invoiceForm.description} onChange={e => setInvoiceForm({ ...invoiceForm, description: e.target.value })} className={inputCls} placeholder="e.g. Tuition fee, Registration..." />
              </div>
              {isEditInvoice && (
                <div>
                  <label className="cinema-label block mb-2">Status</label>
                  <select value={invoiceForm.status} onChange={e => setInvoiceForm({ ...invoiceForm, status: e.target.value })} className={selectCls}>
                    <option value="pending">Pending</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowInvoiceModal(false)} className="flex-1 cinema-btn-ghost text-xs py-2.5 min-h-0">Cancel</button>
                <button type="submit" className="flex-1 cinema-btn-primary text-xs py-2.5 min-h-0">
                  {isEditInvoice ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ PAYMENT MODAL ═══ */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-elevated w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <span className="cinema-label block mb-1">{editingPaymentId ? 'Edit' : 'New'}</span>
                <h2 className="font-display text-lg text-white font-light">{editingPaymentId ? 'Edit Payment' : 'Record Payment'}</h2>
              </div>
              <button onClick={() => setShowPaymentModal(false)} className="p-2 text-cinema-muted hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <form onSubmit={handleSavePayment} className="p-6 space-y-4">
              <div>
                <label className="cinema-label block mb-2">Invoice *</label>
                <select
                  required
                  value={paymentForm.invoice_id}
                  onChange={e => {
                    const invId = e.target.value
                    const chosen = invoices.find(i => String(i.id) === String(invId))
                    if (chosen && isAdmissionFeeInvoice(chosen) && chosen.status !== 'paid') {
                      setShowPaymentModal(false)
                      setAdmissionPaymentInvoice(chosen)
                      return
                    }
                    setPaymentForm({
                      ...paymentForm,
                      invoice_id: invId,
                      amount: chosen ? (chosen.amount - (chosen.amount_paid || 0)) : paymentForm.amount,
                      transaction_reference: generateRef(paymentForm.payment_method, chosen)
                    })
                  }}
                  className={selectCls}
                >
                  <option value="">Select invoice...</option>
                  {invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').map(inv => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoice_number} — {inv.student_name} (Balance: {formatCurrency(inv.amount - (inv.amount_paid || 0))})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="cinema-label block mb-2">Amount (UGX) *</label>
                  <input required type="number" min="1" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} className={inputCls} placeholder="0" />
                </div>
                <div>
                  <label className="cinema-label block mb-2">Payment Date *</label>
                  <input required type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="cinema-label block mb-2">Payment Method *</label>
                <select required value={paymentForm.payment_method}
                  onChange={e => {
                    const method = e.target.value
                    const inv = invoices.find(i => String(i.id) === String(paymentForm.invoice_id)) || null
                    setPaymentForm({ ...paymentForm, payment_method: method, transaction_reference: generateRef(method, inv) })
                  }}
                  className={selectCls}
                >
                  <option value="cash">Cash</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="cinema-label block mb-2">Transaction Reference</label>
                <input type="text" value={paymentForm.transaction_reference} onChange={e => setPaymentForm({ ...paymentForm, transaction_reference: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="cinema-label block mb-2">Notes</label>
                <textarea rows={2} value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} className={inputCls} placeholder="Optional notes..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowPaymentModal(false)} className="flex-1 cinema-btn-ghost text-xs py-2.5 min-h-0">Cancel</button>
                <button type="submit" className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-400 text-white text-xs font-sans transition-all">
                  {editingPaymentId ? 'Update Payment' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ CONFIRM ADMISSION FEE PAYMENT MODAL ═══ */}
      <ConfirmAdmissionPaymentModal
        isOpen={Boolean(admissionPaymentInvoice)}
        invoice={admissionPaymentInvoice}
        onClose={() => setAdmissionPaymentInvoice(null)}
        onSuccess={() => {
          fetchData()
        }}
      />

      {/* ═══ EXPENSE MODAL ═══ */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <span className="cinema-label block mb-1">{editingExpenseId ? 'Edit' : 'New'}</span>
                <h2 className="font-display text-lg text-white font-light">{editingExpenseId ? 'Edit Expense' : 'Record Expense'}</h2>
              </div>
              <button onClick={resetExpenseForm} className="p-2 text-cinema-muted hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <form onSubmit={handleSaveExpense} className="p-6 space-y-4">
              <div>
                <label className="cinema-label block mb-2">Title *</label>
                <input required type="text" value={expenseForm.title} onChange={e => setExpenseForm({ ...expenseForm, title: e.target.value })} className={inputCls} placeholder="e.g. Monthly Rent, Staff Salaries" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="cinema-label block mb-2">Category *</label>
                  <select required value={expenseForm.category} onChange={e => setExpenseForm({ ...expenseForm, category: e.target.value })} className={selectCls}>
                    {Object.entries(EXPENSE_CATEGORIES).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="cinema-label block mb-2">Amount (UGX) *</label>
                  <input required type="number" min="0" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} className={inputCls} placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="cinema-label block mb-2">Date *</label>
                  <input required type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm({ ...expenseForm, expense_date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="cinema-label block mb-2">Payment Method</label>
                  <select value={expenseForm.payment_method} onChange={e => setExpenseForm({ ...expenseForm, payment_method: e.target.value })} className={selectCls}>
                    <option value="cash">Cash</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="cinema-label block mb-2">Vendor / Payee</label>
                  <input type="text" value={expenseForm.vendor} onChange={e => setExpenseForm({ ...expenseForm, vendor: e.target.value })} className={inputCls} placeholder="e.g. UMEME, NWSC" />
                </div>
                <div>
                  <label className="cinema-label block mb-2">Receipt / Reference</label>
                  <input type="text" value={expenseForm.receipt_reference} onChange={e => setExpenseForm({ ...expenseForm, receipt_reference: e.target.value })} className={inputCls} placeholder="Receipt number" />
                </div>
              </div>
              <div>
                <label className="cinema-label block mb-2">Notes</label>
                <textarea rows={2} value={expenseForm.notes} onChange={e => setExpenseForm({ ...expenseForm, notes: e.target.value })} className={inputCls} placeholder="Optional notes..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={resetExpenseForm} className="flex-1 cinema-btn-ghost text-xs py-2.5 min-h-0">Cancel</button>
                <button type="submit" className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-400 text-white text-xs font-sans transition-all">
                  {editingExpenseId ? 'Update Expense' : 'Record Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ PROFILE MODAL ═══ */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-elevated w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                <div>
                  <User size={16} className="text-cinema-gold" />
                </div>
                <div>
                  <span className="cinema-label block mb-0.5">Account</span>
                  <h2 className="font-display text-lg text-white font-light">My Profile</h2>
                </div>
              </div>
              <button onClick={() => setShowProfileModal(false)} className="p-2 text-cinema-muted hover:text-white transition-colors"><X size={18} /></button>
            </div>

            <div className="p-6">
              {/* Profile info form */}
              <form onSubmit={handleSaveProfileInfo} className="space-y-4 mb-6">
                <p className="cinema-label block mb-4">Personal Info</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="cinema-label block mb-2">First Name</label>
                    <input type="text" value={profileForm.first_name} onChange={e => setProfileForm(p => ({...p, first_name: e.target.value}))} className={inputCls} required />
                  </div>
                  <div>
                    <label className="cinema-label block mb-2">Last Name</label>
                    <input type="text" value={profileForm.last_name} onChange={e => setProfileForm(p => ({...p, last_name: e.target.value}))} className={inputCls} required />
                  </div>
                </div>
                <div>
                  <label className="cinema-label block mb-2">Email</label>
                  <input type="email" value={profileForm.email} disabled className={inputCls + ' opacity-40 cursor-not-allowed'} />
                </div>
                <div>
                  <label className="cinema-label block mb-2">Phone</label>
                  <input type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({...p, phone: e.target.value}))} className={inputCls} placeholder="e.g. 0712345678" />
                </div>
                <div className="flex justify-end">
                  <button type="submit" disabled={profileSaving} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 disabled:opacity-50">
                    <Save size={13} />{profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>

              <div className="cinema-divider my-6" />

              {/* Password form */}
              <form onSubmit={handleSavePassword} className="space-y-4">
                <p className="cinema-label flex items-center gap-2 mb-4"><Lock size={10} />Change Password</p>
                {[['Current Password', 'current_password', showCurPwd, setShowCurPwd],
                  ['New Password', 'new_password', showNewPwd, setShowNewPwd],
                  ['Confirm New Password', 'confirm_password', showConPwd, setShowConPwd]
                ].map(([label, field, show, setShow]) => (
                  <div key={field}>
                    <label className="cinema-label block mb-2">{label}</label>
                    <div className="relative">
                      <input type={show ? 'text' : 'password'} value={pwdForm[field]} onChange={e => setPwdForm(p => ({...p, [field]: e.target.value}))} className={inputCls + ' pr-10'} />
                      <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-cinema-muted hover:text-white transition-colors">{show ? <EyeOff size={14}/> : <Eye size={14}/>}</button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <button type="submit" disabled={profileSaving} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 disabled:opacity-50">
                    <Lock size={13} />{profileSaving ? 'Saving...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DELETE CONFIRMATION ═══ */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass border border-red-500/20 w-full max-w-sm p-6 rounded-sm" style={{ boxShadow: '0 0 80px rgba(239,68,68,0.08)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/10 rounded-sm">
                <Trash2 size={16} className="text-red-400" />
              </div>
              <h2 className="font-display text-base text-white font-light">Confirm Delete</h2>
            </div>
            <p className="font-sans text-sm text-cinema-muted mb-6">
              This action cannot be undone. Are you sure you want to delete this record?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 cinema-btn-ghost text-xs py-2.5 min-h-0">Cancel</button>
              <button
                onClick={() => {
                  if (deleteConfirm.startsWith('inv-')) handleDeleteInvoice(parseInt(deleteConfirm.replace('inv-', '')))
                  else if (deleteConfirm.startsWith('pay-')) handleDeletePayment(parseInt(deleteConfirm.replace('pay-', '')))
                  else if (deleteConfirm.startsWith('exp-')) handleDeleteExpense(parseInt(deleteConfirm.replace('exp-', '')))
                }}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-400 text-white text-xs font-sans transition-all"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ REJECT PAYMENT SUBMISSION MODAL ═══ */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-strong border border-red-500/20 w-full max-w-md p-6 rounded-sm">
            <div className="flex items-center justify-between pb-4 border-b border-white/8 mb-4">
              <div>
                <h2 className="font-display text-base text-white font-light">Reject Payment Submission</h2>
                <p className="font-sans text-xs text-cinema-muted">
                  Invoice #{rejectModal.invoice_number} · {rejectModal.first_name} {rejectModal.last_name}
                </p>
              </div>
              <button onClick={() => setRejectModal(null)} className="text-cinema-muted hover:text-white">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleRejectSubmission} className="space-y-4">
              <div>
                <p className="font-sans text-xs text-cinema-muted mb-2">
                  Amount: <span className="text-white font-medium">{formatCurrency(rejectModal.amount)}</span> · Ref: <span className="text-cinema-glow font-mono">{rejectModal.transaction_reference}</span>
                </p>
              </div>

              <div>
                <label className="cinema-label block mb-1">Reason for Rejection *</label>
                <textarea
                  required
                  rows={3}
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. Transaction ID not found on bank statement, wrong amount sent..."
                  className="w-full bg-cinema-graphite border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setRejectModal(null)}
                  className="flex-1 cinema-btn-ghost text-xs py-2.5 min-h-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processingSubId === rejectModal.id}
                  className="flex-1 px-5 py-2.5 bg-red-500 hover:bg-red-400 text-white text-xs font-sans rounded-sm transition-all flex items-center justify-center gap-2"
                >
                  {processingSubId === rejectModal.id ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Official Receipt Viewer & Downloader Modal */}
      <ReceiptModal
        paymentId={selectedReceiptPaymentId}
        isOpen={Boolean(selectedReceiptPaymentId)}
        onClose={() => setSelectedReceiptPaymentId(null)}
      />
      <InvoiceModal
        invoice={previewInvoice}
        isOpen={Boolean(previewInvoice)}
        onClose={() => setPreviewInvoice(null)}
      />
    </div>
  )
}
